// Mobile stock screen (Phase 1 + ad-hoc / stocktake).
//   After an outlet is picked, staff chooses ONE of three flows:
//     1. Receive Stock (ad-hoc) — scan → key qty received → +stock
//     2. Stocktake              — scan → shows current → key counted → set
//     3. Pending Deliveries     — scan against an existing dispatch doc
//
//   Each ledger entry is stamped with staff name + timestamp + idempotency
//   key so a flaky mobile network can replay without double-posting.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { api } from "@/staff/api";
import { useAuth } from "@/staff/AuthContext";
import {
  Camera, Check, X, Package, Loader2, AlertTriangle, ArrowLeft,
  Inbox, ClipboardList,
} from "lucide-react";

const btnPrimary = "inline-flex items-center justify-center gap-2 px-6 py-3 bg-ink text-bone text-xs uppercase tracking-[0.22em] rounded-full disabled:opacity-40";

export default function Receive() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [outlets, setOutlets] = useState([]);
  const [activeOutlet, setActiveOutlet] = useState("");
  const [deliveries, setDeliveries] = useState([]);
  const [picked, setPicked] = useState(null); // delivery doc
  const [mode, setMode] = useState(null); // "receive" | "stocktake" | null
  const [err, setErr] = useState("");

  const myOutlets = (user?.outlet_codes || []);
  const canSeeAll = user?.role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/inv/outlets");
        const visible = canSeeAll ? data : data.filter((o) => myOutlets.includes(o.code));
        setOutlets(visible);
        if (!activeOutlet && visible.length === 1) setActiveOutlet(visible[0].code);
      } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    })();
  }, []); // eslint-disable-line

  const loadDeliveries = async () => {
    if (!activeOutlet) { setDeliveries([]); return; }
    try {
      const { data } = await api.get(`/inv/deliveries?outlet=${activeOutlet}`);
      setDeliveries(data.filter((d) => ["dispatched", "partial"].includes(d.status)));
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { loadDeliveries(); /* eslint-disable-next-line */ }, [activeOutlet]);

  if (picked) {
    return (
      <ReceivingSession
        delivery={picked}
        onBack={async () => { setPicked(null); await loadDeliveries(); }}
      />
    );
  }

  if (mode === "receive" || mode === "stocktake") {
    return (
      <ScanLoopSession
        outletCode={activeOutlet}
        outletName={outlets.find((o) => o.code === activeOutlet)?.name || ""}
        mode={mode}
        onBack={() => setMode(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="receive-root">
      <header className="sticky top-0 z-10 bg-bone border-b border-ink/10 px-4 py-3 flex items-center justify-between">
        <div>
          <p className="overline text-ink-soft text-[9px]">Matcha Story · Stock</p>
          <h1 className="font-display text-lg text-ink">{user?.preferred_name || user?.full_name || "Staff"}</h1>
        </div>
        <button type="button" data-testid="receive-logout" onClick={() => { logout(); nav("/staff/login"); }} className="text-ink-soft hover:text-ink text-xs">
          Sign out
        </button>
      </header>

      <main className="px-4 py-5 max-w-xl mx-auto space-y-5">
        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm" data-testid="receive-err">{err}</p>}

        <section>
          <p className="overline text-ink-soft mb-2">Active outlet</p>
          {outlets.length === 0 ? (
            <p className="text-sm text-ink-soft bg-amber-50 border border-amber-200 px-3 py-3 rounded-sm" data-testid="receive-no-outlet">
              You are not assigned to any outlet yet. Ask HQ to authorise your account under <strong>Admin → Team → Set Employment → Assigned Outlets</strong>.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2" data-testid="receive-outlet-picker">
              {outlets.map((o) => (
                <button
                  key={o.code}
                  type="button"
                  data-testid={`receive-outlet-${o.code}`}
                  onClick={() => setActiveOutlet(o.code)}
                  className={`px-4 py-3 rounded-sm text-left transition-colors ${
                    activeOutlet === o.code
                      ? "bg-ink text-bone border-ink"
                      : "bg-white text-ink border border-ink/15 hover:border-matcha-dark"
                  } border`}
                >
                  <p className="font-mono text-xs opacity-80">{o.code}</p>
                  <p className="font-display text-base">{o.name}</p>
                </button>
              ))}
            </div>
          )}
        </section>

        {activeOutlet && (
          <>
            <section>
              <p className="overline text-ink-soft mb-2">What are you doing today?</p>
              <div className="grid grid-cols-1 gap-3" data-testid="receive-mode-picker">
                <button
                  type="button"
                  data-testid="receive-mode-receive"
                  onClick={() => setMode("receive")}
                  className="w-full flex items-center gap-4 bg-matcha-dark text-bone px-5 py-4 rounded-sm hover:opacity-95 text-left"
                >
                  <Inbox size={28} />
                  <div className="flex-1">
                    <p className="font-display text-lg">Receive Stock</p>
                    <p className="text-[11px] opacity-80">Scan QR → key qty received → adds to stock</p>
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="receive-mode-stocktake"
                  onClick={() => setMode("stocktake")}
                  className="w-full flex items-center gap-4 bg-white border-2 border-ink/15 hover:border-matcha-dark px-5 py-4 rounded-sm text-left"
                >
                  <ClipboardList size={28} className="text-matcha-dark" />
                  <div className="flex-1">
                    <p className="font-display text-lg text-ink">Stocktake</p>
                    <p className="text-[11px] text-ink-soft">Scan QR → see current → key counted qty → overwrites stock</p>
                  </div>
                </button>
              </div>
            </section>

            <section>
              <p className="overline text-ink-soft mb-2">Pending deliveries · {deliveries.length}</p>
              {deliveries.length === 0 ? (
                <p className="text-sm text-ink-soft italic text-center py-6" data-testid="receive-no-deliveries">
                  No HQ dispatch pending for {activeOutlet}. Use <strong>Receive Stock</strong> above for ad-hoc deliveries.
                </p>
              ) : (
                <ul className="space-y-2" data-testid="receive-delivery-list">
                  {deliveries.map((d) => {
                    const outstanding = d.lines.reduce((s, l) => s + Math.max(0, l.qty_dispatched - l.qty_received - l.qty_damaged - l.qty_missing), 0);
                    return (
                      <li key={d.id}>
                        <button
                          type="button"
                          data-testid={`receive-pick-${d.id}`}
                          onClick={() => setPicked(d)}
                          className="w-full flex items-center justify-between gap-3 bg-white px-4 py-3 rounded-sm border border-ink/10 hover:border-matcha-dark text-left"
                        >
                          <div>
                            <p className="font-mono text-xs text-ink-soft">{d.id}</p>
                            <p className="font-display text-base text-ink">{d.lines.length} line{d.lines.length === 1 ? "" : "s"} · {outstanding} outstanding</p>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-amber-700">{d.status}</p>
                          </div>
                          <Package size={28} className="text-matcha-dark" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}


// -------------------------------------------------------------
// Shared ad-hoc / stocktake scanning session.
// -------------------------------------------------------------
function ScanLoopSession({ outletCode, outletName, mode, onBack }) {
  const isReceive = mode === "receive";
  const [scanning, setScanning] = useState(false);
  const [lookup, setLookup] = useState(null); // {sku, name, base_unit, qty_on_hand, last_movement}
  const [qty, setQty] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]); // last 5 successful entries this session
  const [lastScan, setLastScan] = useState("");

  const handleScan = async (code) => {
    setErr("");
    setLastScan(code);
    const raw = (code || "").trim();
    const sku = raw.toUpperCase().startsWith("MS-SKU:") ? raw.split(":", 2)[1] : raw;
    try {
      const { data } = await api.get(`/inv/balance/${outletCode}/${encodeURIComponent(sku)}`);
      setLookup(data);
      setQty("");
      setRemarks("");
    } catch (e) {
      setErr(e?.response?.data?.detail || `SKU "${sku}" not found`);
      setLookup(null);
    }
  };

  const submit = async () => {
    const n = Number(qty);
    if (!Number.isFinite(n) || n < 0) { setErr("Enter a quantity"); return; }
    if (isReceive && n <= 0) { setErr("Qty received must be more than zero"); return; }
    setBusy(true); setErr("");
    try {
      const body = isReceive
        ? { outlet_code: outletCode, sku: lookup.sku, qty: n, remarks, idempotency_key: crypto.randomUUID() }
        : { outlet_code: outletCode, sku: lookup.sku, counted_qty: n, remarks, idempotency_key: crypto.randomUUID() };
      const url = isReceive ? "/inv/receive-adhoc" : "/inv/stocktake";
      const { data } = await api.post(url, body);
      setHistory((h) => [{
        sku:  lookup.sku,
        name: lookup.name,
        unit: lookup.base_unit,
        before: lookup.qty_on_hand,
        after:  data.qty_on_hand,
        delta:  data.entry.qty_delta,
        when:   data.entry.created_at,
        who:    data.entry.staff_name,
        kind:   isReceive ? "Received" : "Counted",
      }, ...h].slice(0, 10));
      setLookup(null);
      setQty("");
      setRemarks("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone pb-10" data-testid={`scan-session-${mode}`}>
      <header className="sticky top-0 z-10 bg-bone border-b border-ink/10 px-4 py-3 flex items-center justify-between">
        <button type="button" data-testid="scan-back" onClick={onBack} className="text-ink-soft hover:text-ink inline-flex items-center gap-1 text-xs">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="text-right">
          <p className="font-mono text-[11px] text-ink-soft">{outletCode}</p>
          <p className="font-display text-sm text-ink">
            {isReceive ? "Receive Stock" : "Stocktake"} · {outletName}
          </p>
        </div>
      </header>

      <main className="px-4 py-4 max-w-xl mx-auto space-y-4">
        {err && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm flex items-center gap-2" data-testid="scan-err">
            <AlertTriangle size={14} /> {err}
          </p>
        )}

        {scanning ? (
          <ScannerFrame
            onDetected={(code) => { setScanning(false); handleScan(code); }}
            onClose={() => setScanning(false)}
            caption={isReceive ? "Scan the product QR to add stock" : "Scan the product QR to count stock"}
          />
        ) : !lookup ? (
          <>
            <button
              type="button"
              data-testid="scan-open"
              onClick={() => setScanning(true)}
              className={`${btnPrimary} w-full ${isReceive ? "bg-matcha-dark" : "bg-ink"}`}
            >
              <Camera size={16} /> Scan Product QR
            </button>
            <ManualSkuEntry onLookup={handleScan} />
          </>
        ) : null}

        {lastScan && !scanning && !lookup && (
          <p className="text-[11px] text-ink-soft text-center font-mono" data-testid="scan-last-code">
            Last scan: {lastScan}
          </p>
        )}

        {lookup && (
          <section className="bg-white rounded-sm border border-ink/15 p-5 space-y-4" data-testid="scan-detail">
            <div>
              <p className="font-mono text-[11px] text-ink-soft">{lookup.sku}</p>
              <h3 className="font-display text-lg text-ink">{lookup.name}</h3>
            </div>
            <div className="flex items-end justify-between bg-cream/50 px-4 py-3 rounded-sm">
              <div>
                <p className="overline text-ink-soft text-[9px]">Current stock</p>
                <p className="font-display text-3xl text-ink" data-testid="scan-current-qty">
                  {lookup.qty_on_hand} <span className="text-sm text-ink-soft">{lookup.base_unit}</span>
                </p>
              </div>
              {lookup.last_movement && (
                <div className="text-right">
                  <p className="overline text-ink-soft text-[9px]">Last movement</p>
                  <p className="text-[11px] text-ink">{lookup.last_movement.txn_type.replace(/_/g, " ")}</p>
                  <p className="text-[10px] text-ink-soft">by {lookup.last_movement.staff_name || "—"}</p>
                  <p className="text-[10px] text-ink-soft font-mono">{(lookup.last_movement.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              )}
            </div>
            <div>
              <label className="overline text-ink-soft text-[9px] block mb-1">
                {isReceive ? `Qty received (${lookup.base_unit})` : `Counted qty (${lookup.base_unit})`}
              </label>
              <input
                data-testid="scan-qty"
                autoFocus
                type="number"
                min="0"
                step="0.01"
                placeholder={isReceive ? "e.g. 100" : `e.g. ${lookup.qty_on_hand}`}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-full px-4 py-3 rounded-sm border border-ink/20 bg-white text-lg text-ink"
              />
              {!isReceive && qty !== "" && Number.isFinite(Number(qty)) && (
                <p className="text-[11px] text-ink-soft mt-1" data-testid="scan-stocktake-delta">
                  System will adjust by{" "}
                  <strong className={Number(qty) - lookup.qty_on_hand < 0 ? "text-red-700" : "text-matcha-dark"}>
                    {(Number(qty) - lookup.qty_on_hand) >= 0 ? "+" : ""}{(Number(qty) - lookup.qty_on_hand).toFixed(2)}
                  </strong>{" "}
                  {lookup.base_unit}
                </p>
              )}
            </div>
            <div>
              <label className="overline text-ink-soft text-[9px] block mb-1">Remarks (optional)</label>
              <input
                data-testid="scan-remarks"
                type="text"
                placeholder={isReceive ? "e.g. from Ippodo supplier" : "e.g. monthly stocktake"}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="w-full px-4 py-2 rounded-sm border border-ink/15 bg-white text-sm text-ink"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="scan-cancel"
                onClick={() => { setLookup(null); setQty(""); setRemarks(""); }}
                className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink px-3 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="scan-more"
                onClick={() => { setLookup(null); setQty(""); setRemarks(""); setErr(""); setScanning(true); }}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-ink/20 text-ink text-xs uppercase tracking-[0.18em] rounded-full hover:border-matcha-dark"
              >
                <Camera size={14} /> Scan More
              </button>
              <button
                type="button"
                data-testid="scan-save"
                onClick={submit}
                disabled={busy || qty === ""}
                className={`${btnPrimary} flex-1 ${isReceive ? "bg-matcha-dark" : "bg-ink"}`}
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                {isReceive ? "Save receipt" : "Save count"}
              </button>
            </div>
          </section>
        )}

        {history.length > 0 && (
          <section data-testid="scan-history">
            <p className="overline text-ink-soft mb-2">This session · {history.length}</p>
            <ul className="space-y-1.5">
              {history.map((h, i) => (
                <li key={i} className="bg-white rounded-sm border border-ink/10 px-3 py-2 flex items-center justify-between text-xs">
                  <div>
                    <p className="font-mono text-[10px] text-ink-soft">{h.sku}</p>
                    <p className="text-ink">{h.name}</p>
                    <p className="text-[10px] text-ink-soft">by {h.who} · {(h.when || "").slice(11, 16)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-ink-soft">{h.kind}</p>
                    <p className="font-mono text-ink">
                      {h.before} → <strong>{h.after}</strong> {h.unit}
                    </p>
                    <p className={`font-mono text-[10px] ${h.delta < 0 ? "text-red-700" : "text-matcha-dark"}`}>
                      {h.delta >= 0 ? "+" : ""}{h.delta}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}


// -------------------------------------------------------------
// Delivery-doc receiving (unchanged from Phase 1).
// -------------------------------------------------------------
function ReceivingSession({ delivery, onBack }) {
  const [state, setState] = useState(() => Object.fromEntries(
    delivery.lines.map((l) => [l.sku, {
      ...l,
      outstanding: Math.max(0, l.qty_dispatched - l.qty_received - l.qty_damaged - l.qty_missing),
      qty_to_receive: "",
      condition: "good",
      remarks: "",
    }])
  ));
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [err, setErr] = useState("");
  const idemRef = useRef(crypto.randomUUID());

  const scanLines = Object.values(state);

  const handleScan = (code) => {
    setLastScan(code);
    const raw = (code || "").trim();
    const sku = raw.toUpperCase().startsWith("MS-SKU:") ? raw.split(":", 2)[1] : raw;
    const hit = Object.values(state).find((l) => l.sku === sku);
    if (!hit) {
      setErr(`SKU ${sku} is not on this delivery`);
      setTimeout(() => setErr(""), 2500);
      return;
    }
    setErr("");
    const node = document.getElementById(`qty-${sku}`);
    if (node) { node.focus(); node.select(); }
  };

  const update = (sku, patch) => setState((s) => ({ ...s, [sku]: { ...s[sku], ...patch } }));

  const submit = async () => {
    const payload = scanLines
      .filter((l) => Number(l.qty_to_receive) > 0)
      .map((l) => ({
        sku: l.sku,
        qty_received: Number(l.qty_to_receive),
        condition: l.condition,
        remarks: l.remarks,
      }));
    if (payload.length === 0) { setErr("Enter at least one quantity to submit"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/inv/deliveries/${delivery.id}/receive`, {
        idempotency_key: idemRef.current,
        lines: payload,
      });
      setSubmitted(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <ReceiveSuccess delivery={submitted.delivery} onBack={onBack} />
    );
  }

  return (
    <div className="min-h-screen bg-bone pb-20" data-testid="receive-session">
      <header className="sticky top-0 z-10 bg-bone border-b border-ink/10 px-4 py-3 flex items-center justify-between">
        <button type="button" data-testid="receive-back" onClick={onBack} className="text-ink-soft hover:text-ink inline-flex items-center gap-1 text-xs">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="text-right">
          <p className="font-mono text-[11px] text-ink-soft">{delivery.id}</p>
          <p className="font-display text-sm text-ink">{delivery.outlet_code} · {delivery.outlet_name}</p>
        </div>
      </header>

      <main className="px-4 py-4 max-w-xl mx-auto space-y-4">
        {err && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm flex items-center gap-2" data-testid="receive-session-err">
            <AlertTriangle size={14} /> {err}
          </p>
        )}

        {scanning ? (
          <ScannerFrame
            onDetected={(code) => { handleScan(code); setScanning(false); }}
            onClose={() => setScanning(false)}
            caption={`Scan a QR from ${delivery.id}`}
          />
        ) : (
          <>
            <button
              type="button"
              data-testid="receive-scan-open"
              onClick={() => setScanning(true)}
              className={`${btnPrimary} w-full bg-matcha-dark`}
            >
              <Camera size={16} /> Scan Product QR
            </button>
            <ManualSkuEntry onLookup={handleScan} />
          </>
        )}
        {lastScan && !scanning && (
          <p className="text-[11px] text-ink-soft text-center font-mono" data-testid="receive-last-scan">Last scan: {lastScan}</p>
        )}

        <section className="space-y-3">
          {scanLines.map((l) => (
            <div key={l.sku} className="bg-white rounded-sm border border-ink/10 p-4" data-testid={`receive-line-${l.sku}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-mono text-[11px] text-ink-soft">{l.sku}</p>
                  <p className="font-display text-base text-ink">{l.qty_dispatched} {l.unit} dispatched · {l.outstanding} outstanding</p>
                </div>
                {l.outstanding === 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800">DONE</span>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  id={`qty-${l.sku}`}
                  data-testid={`receive-qty-${l.sku}`}
                  type="number"
                  min="0"
                  max={l.outstanding}
                  step="0.01"
                  placeholder={`Qty received (max ${l.outstanding})`}
                  value={l.qty_to_receive}
                  disabled={l.outstanding === 0}
                  onChange={(e) => update(l.sku, { qty_to_receive: e.target.value })}
                  className="px-3 py-2 rounded-sm border border-ink/15 bg-white text-sm text-ink"
                />
                <select
                  data-testid={`receive-cond-${l.sku}`}
                  value={l.condition}
                  onChange={(e) => update(l.sku, { condition: e.target.value })}
                  className="px-3 py-2 rounded-sm border border-ink/15 bg-white text-sm text-ink"
                >
                  <option value="good">Good</option>
                  <option value="damaged">Damaged</option>
                  <option value="missing">Missing</option>
                  <option value="other">Other</option>
                </select>
              </div>
              {l.condition !== "good" && (
                <input
                  type="text"
                  data-testid={`receive-remarks-${l.sku}`}
                  placeholder="Remarks (what happened?)"
                  value={l.remarks}
                  onChange={(e) => update(l.sku, { remarks: e.target.value })}
                  className="w-full mt-2 px-3 py-2 rounded-sm border border-ink/15 bg-white text-sm text-ink"
                />
              )}
            </div>
          ))}
        </section>
      </main>

      <footer className="fixed bottom-0 inset-x-0 bg-bone border-t border-ink/10 px-4 py-3">
        <button
          type="button"
          data-testid="receive-submit"
          onClick={submit}
          disabled={busy}
          className={`${btnPrimary} w-full max-w-xl mx-auto`}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          Confirm Receipt
        </button>
      </footer>
    </div>
  );
}


function ScannerFrame({ onDetected, onClose, caption = "Scan the product QR" }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    const reader = new BrowserMultiFormatReader();
    (async () => {
      try {
        const controls = await reader.decodeFromVideoDevice(
          undefined,
          videoRef.current,
          (result) => {
            if (cancelled || !result) return;
            onDetected(result.getText());
          },
        );
        controlsRef.current = controls;
      } catch (e) {
        setErr("Camera unavailable. Enable camera permission or type the SKU manually.");
      }
    })();
    return () => {
      cancelled = true;
      try { controlsRef.current?.stop(); } catch {}
    };
  }, [onDetected]);

  return (
    <div
      className="relative rounded-3xl overflow-hidden bg-ink shadow-xl"
      data-testid="receive-scanner-frame"
      style={{ aspectRatio: "3/4" }}
    >
      <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline />

      {/* Dim backdrop with a transparent square cutout in the centre.
          We fake the cutout by stacking four dark panels around a 70%-wide
          centred frame — simpler + safer than CSS masks for mobile Safari. */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="relative rounded-3xl border-[3px] border-white/90"
            style={{ width: "70%", aspectRatio: "1/1", boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)" }}
          >
            {/* Corner accents for extra guidance */}
            <span className="absolute -top-[3px] -left-[3px] w-6 h-6 border-t-[5px] border-l-[5px] border-white rounded-tl-3xl" />
            <span className="absolute -top-[3px] -right-[3px] w-6 h-6 border-t-[5px] border-r-[5px] border-white rounded-tr-3xl" />
            <span className="absolute -bottom-[3px] -left-[3px] w-6 h-6 border-b-[5px] border-l-[5px] border-white rounded-bl-3xl" />
            <span className="absolute -bottom-[3px] -right-[3px] w-6 h-6 border-b-[5px] border-r-[5px] border-white rounded-br-3xl" />
          </div>
        </div>
      </div>

      <button
        type="button"
        data-testid="receive-scanner-close"
        onClick={onClose}
        aria-label="Close scanner"
        className="absolute top-3 right-3 bg-white/95 text-ink p-2 rounded-full shadow-md hover:bg-white"
      >
        <X size={16} />
      </button>

      {/* Caption pill, floating bottom-centre */}
      <div className="absolute bottom-4 inset-x-0 flex justify-center px-6 pointer-events-none">
        <p className="bg-ink/80 backdrop-blur-sm text-bone text-xs px-4 py-2 rounded-full text-center shadow">
          {caption}
        </p>
      </div>

      {err && (
        <p className="absolute top-16 inset-x-4 text-xs text-bone bg-red-600/85 px-3 py-2 rounded-sm text-center">
          {err}
        </p>
      )}
    </div>
  );
}


function ManualSkuEntry({ onLookup, placeholder = "e.g. MATCHA-001 or MS-SKU:MATCHA-001" }) {
  const [value, setValue] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const v = (value || "").trim();
    if (!v) return;
    onLookup(v);
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-ink/10 p-4 space-y-2" data-testid="manual-sku-card">
      <p className="text-xs font-medium text-ink">Or enter the SKU</p>
      <div className="flex items-center gap-2">
        <input
          data-testid="manual-sku-input"
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          className="flex-1 px-4 py-3 rounded-xl border border-ink/15 bg-cream/50 text-sm text-ink font-mono focus:outline-none focus:border-matcha-dark"
        />
        <button
          type="submit"
          data-testid="manual-sku-lookup"
          disabled={!value.trim()}
          className="px-5 py-3 bg-matcha-dark text-bone rounded-xl text-xs uppercase tracking-[0.18em] disabled:opacity-40 hover:opacity-95"
        >
          Look up
        </button>
      </div>
    </form>
  );
}


function ReceiveSuccess({ delivery, onBack }) {
  const totR = delivery.lines.reduce((s, l) => s + (l.qty_received || 0), 0);
  const totD = delivery.lines.reduce((s, l) => s + (l.qty_dispatched || 0), 0);
  return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center p-6 text-center" data-testid="receive-success">
      <div className="w-20 h-20 rounded-full bg-matcha-dark/10 flex items-center justify-center mb-5">
        <Check size={38} className="text-matcha-dark" />
      </div>
      <h2 className="font-display text-2xl text-ink mb-2">Received</h2>
      <p className="font-mono text-xs text-ink-soft mb-4">{delivery.id} · {delivery.outlet_code}</p>
      <p className="text-sm text-ink mb-6">
        {totR}/{totD} units confirmed. Status: <strong className="text-matcha-dark">{delivery.status.toUpperCase()}</strong>
      </p>
      <div className="flex gap-3">
        <button type="button" data-testid="receive-done" onClick={onBack} className={btnPrimary}>Back to deliveries</button>
      </div>
    </div>
  );
}
